// The measure tool's analytics events, ported from the Android app's MeasureTelemetry.kt
// (MeasureWire) and PostHogMeasureToolEvents.kt. Event names, property keys and wire values are a
// contract shared with iOS and Android verbatim, so one PostHog question covers every platform.
// Pure builders; the caller sends the result. Never per drag frame: drags are counted and reported
// once, in measure_tool_closed.

import { MeasureBearingUnit, MeasureNorthReference } from './bearings';
import { MeasureGridFormat } from './gridReference';
import { MeasureLock, MeasureStartSource } from './session';

export interface MeasureAnalyticsEvent {
  event: string;
  properties: Record<string, unknown>;
}

export const MEASURE_EVENTS = {
  opened: 'measure_tool_opened',
  startChosen: 'measure_tool_start_chosen',
  pointPlaced: 'measure_tool_point_placed',
  lockChanged: 'measure_tool_lock_changed',
  snapToggled: 'measure_tool_snap_toggled',
  gridCopied: 'measure_tool_grid_copied',
  closed: 'measure_tool_closed',
  failed: 'measure_tool_failed',
} as const;

/** The map screen the tool was opened on, as the `screen` property names it (Android `MeasureScreen`). */
export type MeasureScreen = 'main_map' | 'hike_map' | 'planner' | 'recording' | 'recorded_hike';

/** How the point was placed. */
export type MeasurePlacementMethod = 'tap' | 'range_bearing';
export const METHOD_TAP: MeasurePlacementMethod = 'tap';
export const METHOD_RANGE_BEARING: MeasurePlacementMethod = 'range_bearing';

/** Whether the closing measurement was straight or along the route. */
export type MeasureMode = 'straight' | 'route';
export const MODE_STRAIGHT: MeasureMode = 'straight';
export const MODE_ROUTE: MeasureMode = 'route';

const SOURCE_WIRE: Record<MeasureStartSource, string> = { MyLocation: 'my_location', MapPoint: 'map_point' };
const LOCK_WIRE: Record<MeasureLock, string> = { Free: 'free', Distance: 'distance', Bearing: 'bearing' };
const NORTH_WIRE: Record<MeasureNorthReference, string> = { True: 'true', Magnetic: 'magnetic', Grid: 'grid' };
const UNIT_WIRE: Record<MeasureBearingUnit, string> = { Degrees: 'degrees', Mils: 'mils' };
const GRID_WIRE: Record<MeasureGridFormat, string> = {
  IrishGrid: 'irish_grid',
  Itm: 'itm',
  Osgb: 'osgb',
  Utm: 'utm',
  Mgrs: 'mgrs',
  LatLong: 'lat_long',
};

export const wireSource = (source: MeasureStartSource): string => SOURCE_WIRE[source];
export const wireLock = (lock: MeasureLock): string => LOCK_WIRE[lock];
export const wireNorthReference = (reference: MeasureNorthReference): string => NORTH_WIRE[reference];
export const wireBearingUnit = (unit: MeasureBearingUnit): string => UNIT_WIRE[unit];
export const wireGridFormat = (format: MeasureGridFormat): string => GRID_WIRE[format];

export function openedEvent(screen: MeasureScreen, routeAvailable: boolean): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.opened, properties: { screen, route_available: routeAvailable } };
}

export function startChosenEvent(source: MeasureStartSource): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.startChosen, properties: { source: wireSource(source) } };
}

export function pointPlacedEvent(method: MeasurePlacementMethod): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.pointPlaced, properties: { method } };
}

export function lockChangedEvent(lock: MeasureLock): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.lockChanged, properties: { lock: wireLock(lock) } };
}

export function snapToggledEvent(enabled: boolean): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.snapToggled, properties: { enabled } };
}

export function gridCopiedEvent(format: MeasureGridFormat): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.gridCopied, properties: { grid_format: wireGridFormat(format) } };
}

export interface MeasureClosedInput {
  /** Whole seconds the tool was open: floor((closedAt − openedAt) / 1000), as Android counts them. */
  durationSeconds: number;
  placed: boolean;
  mode: MeasureMode;
  lock: MeasureLock;
  dragCount: number;
  northReference: MeasureNorthReference;
  bearingUnit: MeasureBearingUnit;
  gridFormat: MeasureGridFormat;
}

/** Whole seconds between two epoch-millisecond instants, truncated like Android's Long division. */
export function durationSeconds(openedAtMillis: number, closedAtMillis: number): number {
  return Math.trunc((closedAtMillis - openedAtMillis) / 1000);
}

/** The session, reported once when the tool closes. */
export function closedEvent(input: MeasureClosedInput): MeasureAnalyticsEvent {
  return {
    event: MEASURE_EVENTS.closed,
    properties: {
      duration_s: input.durationSeconds,
      placed: input.placed,
      mode: input.mode,
      lock: wireLock(input.lock),
      drag_count: input.dragCount,
      north_reference: wireNorthReference(input.northReference),
      bearing_unit: wireBearingUnit(input.bearingUnit),
      grid_format: wireGridFormat(input.gridFormat),
    },
  };
}

/** Why the tool failed. The web has no compass, so Android's compass_unavailable has no counterpart. */
export type MeasureFailureReason = 'elevation_fetch_failed' | 'location_unavailable';

/** Which elevation provider failed last: `dem` (offline terrain, with the API answering) or `api`. */
export type MeasureElevationSource = 'dem' | 'api';

/** Why there was no location: Android's REASON_PERMISSION / REASON_NO_FIX. */
export type MeasureLocationFailure = 'permission' | 'no_fix';

/**
 * Elevation could not be read. Android's log fields (`source`, `samples`, `error`) under
 * `reason: 'elevation_fetch_failed'`. The web has no offline terrain, so its source is `api`.
 */
export function elevationFetchFailedEvent(samples: number, error: string | null, source: MeasureElevationSource = 'api'): MeasureAnalyticsEvent {
  return {
    event: MEASURE_EVENTS.failed,
    properties: { reason: 'elevation_fetch_failed', source, samples, error },
  };
}

/**
 * The walker's location could not be used. Android logs its own `reason` (permission / no_fix);
 * here `reason` names the failure, so that value travels as `cause`.
 */
export function locationUnavailableEvent(cause: MeasureLocationFailure): MeasureAnalyticsEvent {
  return { event: MEASURE_EVENTS.failed, properties: { reason: 'location_unavailable', cause } };
}

/**
 * A GeolocationPositionError code as a [MeasureLocationFailure]: PERMISSION_DENIED (1) is
 * `permission`; POSITION_UNAVAILABLE (2) and TIMEOUT (3) are both "no fix", as Android reports them.
 */
export function locationFailureFromGeolocationError(code: number): MeasureLocationFailure {
  return code === 1 ? 'permission' : 'no_fix';
}

/** How old a fix may be to start a measurement from (Android `MeasureFixFreshness`). */
export const FIX_MAX_AGE_MILLIS = 30_000;

/** Whether a fix taken at [fixMillis] is recent enough at [nowMillis] (epoch ms, e.g. GeolocationPosition.timestamp). */
export function isFixFresh(fixMillis: number, nowMillis: number, maxAgeMillis = FIX_MAX_AGE_MILLIS): boolean {
  return nowMillis - fixMillis <= maxAgeMillis;
}
